import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';
import {
  OrderNumber,
  OrderStatusComponent,
  PaymentStatusComponent,
  RefundedPriceComponent,
  ShipmentStatusComponent,
} from '@web/shared/module/shop';
import { Divider } from 'primeng/divider';
import {
  CurrencyComponent,
  DateComponent,
  DateTimeComponent,
  DetailsViewComponent,
  DrawerContentComponent,
  DrawerTitleComponent,
  ExternalLinkComponent,
  NoDataComponent,
  PrimengButtonDirective,
  PrimengDialogCustomCloseEventDirective,
  PrimengDialogDirective,
  PrimengDividerDirective,
  PrimengDrawerDirective,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import {
  Guid,
  isEmptyString,
  isNil,
  isNotNil,
  Nillable,
  once,
  valueOrDefault,
} from '@web/utils';
import { AdminOrderDataSourceService } from '../../services';
import { AdminOrderDetails } from '../../models/admin-order-details';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { AdminAdditionalShippingComponent } from '../admin-additional-shipping/admin-additional-shipping.component';
import { AdminShipmentSizeTypeComponent } from '../admin-shipment-size-type/admin-shipment-size-type.component';
import { Drawer } from 'primeng/drawer';
import { AdminOrderShippingAddressEditComponent } from '../admin-order-shipping-address-edit/admin-order-shipping-address-edit.component';
import { AdminInvoiceDataSourceService } from '../../services/admin-invoice-data-source.service';
import { AdminOrderRefundListComponent } from '../admin-order-refund-list/admin-order-refund-list.component';
import { AdminOrderPartialRefundFormComponent } from '../admin-order-partial-refund-form/admin-order-partial-refund-form.component';
import { AdminOrderItemCardComponent } from '../../components/admin-order-item-card/admin-order-item-card.component';
import { AdminOrderShipmentComponentGroupsComponent } from '../../components/admin-order-shipment-component-groups/admin-order-shipment-component-groups.component';

@Component({
  selector: 'app-admin-order-details',
  imports: [
    Divider,
    PrimengDividerDirective,
    SpinnerFilledComponent,
    DetailsViewComponent,
    CurrencyComponent,
    NoDataComponent,
    ShipmentStatusComponent,
    PaymentStatusComponent,
    OrderStatusComponent,
    DateComponent,
    ExternalLinkComponent,
    Button,
    PrimengButtonDirective,
    Dialog,
    PrimengDialogCustomCloseEventDirective,
    AdminAdditionalShippingComponent,
    PrimengDialogDirective,
    AdminShipmentSizeTypeComponent,
    DateTimeComponent,
    Drawer,
    DrawerTitleComponent,
    DrawerContentComponent,
    PrimengDrawerDirective,
    AdminOrderShippingAddressEditComponent,
    RefundedPriceComponent,
    AdminOrderRefundListComponent,
    AdminOrderPartialRefundFormComponent,
    AdminOrderItemCardComponent,
    AdminOrderShipmentComponentGroupsComponent,
  ],
  templateUrl: './admin-order-details.component.html',
  styleUrl: './admin-order-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminOrderDetailsComponent implements OnInit {
  public readonly id = input.required<OrderNumber>();

  protected readonly id$ = toObservable(this.id);
  protected readonly data = signal<Nillable<AdminOrderDetails>>(null);
  protected readonly isNotNil = isNotNil;
  protected readonly additionalShippingDialogVisibility = signal(false);
  protected readonly partialRefundDrawerVisibility = signal(false);
  protected readonly shippingAddressEditVisibility = signal<boolean>(false);
  protected readonly shipmentId = signal<Nillable<Guid>>(null);
  protected readonly isEmptyString = isEmptyString;
  protected readonly isNil = isNil;
  protected readonly valueOrDefault = valueOrDefault;

  private readonly orderDataSource = inject(AdminOrderDataSourceService);
  private readonly invoiceDataSource = inject(AdminInvoiceDataSourceService);

  public ngOnInit(): void {
    this.fetchDetails();
  }

  public handleAdditionalShippingSubmitEvent(): void {
    this.markAdditionalShippingDialogAsInvisible();
    this.fetchDetails();
  }

  public handleAdditionalShippingCancelEvent(): void {
    this.markAdditionalShippingDialogAsInvisible();
  }

  public handleShippingAddressUpdateClick(shipmentId: Guid): void {
    this.shipmentId.set(shipmentId);
    this.showShippingAddressEditDialog();
  }

  public handleSubmitShippingAddressEditEvent(): void {
    this.closeShippingAddressEditDialog();
    this.resolveShipmentId();
    this.fetchDetails();
  }

  public handleDownloadInvoiceClick(invoiceId: Guid): void {
    this.invoiceDataSource.downloadOrderInvoiceById(invoiceId).subscribe();
  }

  public handleAddShippingClick(): void {
    this.markAdditionalShippingDialogAsVisible();
  }

  public handleRemoveButtonClick(id: Guid): void {
    this.orderDataSource
      .confirmAndRemoveShipmentFromOrder({
        orderId: this.id(),
        shipmentId: id,
      })
      .subscribe(() => {
        this.fetchDetails();
      });
  }

  public handleGenerateAndSendButtonClick(id: Guid): void {
    this.orderDataSource
      .confirmAndGenerateAndSend({
        orderId: this.id(),
        shipmentId: id,
      })
      .subscribe(() => {
        this.fetchDetails();
      });
  }

  public handleCancelClick(): void {
    this.orderDataSource
      .confirmAndCancelById(this.id())
      .pipe(once())
      .subscribe(() => this.fetchDetails());
  }

  public handleMarkAsPaidClick(): void {
    this.orderDataSource
      .confirmAndMarkAsPaidById(this.id())
      .pipe(once())
      .subscribe(() => this.fetchDetails());
  }

  public handlePartialRefundClick(): void {
    this.showPartialRefundDrawer();
  }

  public handlePartialRefundSubmitEvent(): void {
    this.closePartialRefundDrawer();
    this.fetchDetails();
  }

  public handlePartialRefundCancelEvent(): void {
    this.closePartialRefundDrawer();
  }

  public handleFullRefundClick(): void {
    this.orderDataSource
      .confirmAndFullRefundOrderById(this.id())
      .pipe(once())
      .subscribe(() => this.fetchDetails());
  }

  public handleCompleteClick(): void {
    this.orderDataSource
      .confirmAndMarkAsCompleteById(this.id())
      .pipe(once())
      .subscribe(() => this.fetchDetails());
  }

  protected readonly additionalShippingDialogCustomCloseEvent = () => {
    this.markAdditionalShippingDialogAsInvisible();
  };

  protected readonly customCloseShippingAddressEditEvent = () => {
    this.closeShippingAddressEditDialog();
    this.resolveShipmentId();
  };

  protected readonly customClosePartialRefundDrawerEvent = () => {
    this.closePartialRefundDrawer();
  };

  protected downloadLabelClick(id: Guid): void {
    this.orderDataSource
      .downloadLabelFor({ shipmentId: id, orderId: this.id() })
      .subscribe();
  }

  protected resolveShipmentId(): void {
    this.shipmentId.set(null);
  }

  private fetchDetails(): void {
    this.id$
      .pipe(
        switchMap((orderNumber) =>
          this.orderDataSource.fetchDetailsById(orderNumber),
        ),
        once(),
      )
      .subscribe((data) => this.data.set(data));
  }

  private markAdditionalShippingDialogAsVisible(): void {
    this.additionalShippingDialogVisibility.set(true);
  }

  private markAdditionalShippingDialogAsInvisible(): void {
    this.additionalShippingDialogVisibility.set(false);
  }

  private closeShippingAddressEditDialog(): void {
    this.shippingAddressEditVisibility.set(false);
  }

  private showShippingAddressEditDialog(): void {
    this.shippingAddressEditVisibility.set(true);
  }

  private closePartialRefundDrawer(): void {
    this.partialRefundDrawerVisibility.set(false);
  }

  private showPartialRefundDrawer(): void {
    this.partialRefundDrawerVisibility.set(true);
  }
}
